import { CharacterBase } from '@/models/character';
import { Hitbox } from '@/models/hitbox';
import { Move } from '@/models/move';
import { MoveType } from '@/models/move-type';
import * as Sentry from '@sentry/browser';

export function canBeCrouchCanceled(move: Move): boolean {
  const allowedTypes = [
    MoveType.air,
    MoveType.grounded,
    MoveType.special,
    MoveType.tilt,
    MoveType.kirbySpecial,
    MoveType.edgeAttack,
    MoveType.tech,
    MoveType.unknown,
  ];
  return allowedTypes.includes(move.type) && !!move.hits && move.hits.length > 0;
}

export function isCrouchCancelPossible(hitbox: Hitbox): boolean {
  return (hitbox.angle > 0 && hitbox.angle < 180) || hitbox.angle == 361;
}

export function getCrouchCancelImpossibleReason(hitbox: Hitbox): string {
  return hitbox.angle > 179 && hitbox.angle != 361
    ? `Cannot use ASDI Down due to the angle being 180 or higher: (${hitbox.angle})`
    : 'Cannot use ASDI Down due to angle being 0';
}

export function calculateCrouchCancelPercentage(
  hitbox: Hitbox,
  target: CharacterBase,
  knockbackTarget: number,
  floor: boolean,
  display999PercentForNeverBreaks: boolean,
  staleness: number,
): string {
  if (hitbox.setKnockback) {
    return setKnockbackCalculation(hitbox, target, knockbackTarget, display999PercentForNeverBreaks);
  }

  if (hitbox.knockbackGrowth === 0) {
    return display999PercentForNeverBreaks ? '999%' : 'Never breaks';
  }

  const staleDamageReduction = staleness * hitbox.damage;
  const staleDamage = hitbox.damage - staleDamageReduction;

  const percentage =
    ((100 + target.characterStatistics.weight) / 14) *
      (((100 / hitbox.knockbackGrowth) * (knockbackTarget - hitbox.baseKnockback) - 18) / (hitbox.damage + 2)) -
    staleDamage;

  if (Infinity === percentage) {
    Sentry.captureMessage(
      `Crouch cancel calculation resulted in Infinity for ${hitbox.id} for target ${target.fightCoreId}`,
    );

    return display999PercentForNeverBreaks ? '999%' : 'Never breaks';
  }

  if (percentage > 0) {
    if (floor) {
      // Melee uses floored percentages within its calculation for the knockback.
      // Because of that, we need to do the reverse and grab the ceiling of the percentage.
      // In Fox's Jab 1 vs Falcon the percentage is 243.71%.
      // This means that 243 is not enough to break crouch cancel but 244% is.
      return Math.ceil(percentage) + '%';
    }

    return percentage.toFixed(2) + '%';
  }

  return '0%';
}

export function meetsKnockbackTarget(hitbox: Hitbox, character: CharacterBase, knockbackTarget: number): boolean {
  // Weight dependant set knockback formula as found on the following sources:
  // eslint-disable-next-line max-len
  // - IKneeData: https://github.com/schmooblidon/schmooblidon.github.io/blob/09c8d4303ce6d98d62918073b474099b5ed9a026/calculatormaths.js#L101
  // - standardtoaster/magus on Smashboards: https://smashboards.com/threads/melee-knockback-values.334245/post-15368915
  const knockback =
    (((hitbox.setKnockback * 10) / 20 + 1) * 1.4 * (200 / (character.characterStatistics.weight + 100)) + 18) *
      (hitbox.knockbackGrowth / 100) +
    hitbox.baseKnockback;

  // The move can be CCed/ASDIed by the given character if the knockback target is NOT met.
  return knockback >= knockbackTarget;
}

function setKnockbackCalculation(
  hitbox: Hitbox,
  target: CharacterBase,
  knockbackTarget: number,
  display999PercentForNeverBreaks: boolean,
): string {
  if (meetsKnockbackTarget(hitbox, target, knockbackTarget)) {
    return '0%';
  } else {
    return display999PercentForNeverBreaks ? '999%' : 'Never breaks';
  }
}
